package com.evoapps.evoshift.domain.model

/**
 * SH-255: what a workplace's employees see of their colleagues' shifts
 * (`workplaces/{id}.settings.rosterVisibility`). Managers and the owner always see everything.
 */
enum class RosterVisibility {
    /** The whole roster (the default, as before SH-255). */
    FULL,

    /** Colleagues' shifts only on the days the person works too, plus open shifts and the marketplace. */
    WORKING_WITH_ME,

    /** Their own shifts, open shifts and the marketplace; colleagues' shifts are not readable. */
    OWN_ONLY;

    companion object {
        /**
         * A stored name: missing is [FULL] (workplaces from before SH-255); a value this app does not
         * know (a newer app's choice) is the most private, [OWN_ONLY], as the rules and the server read it.
         */
        fun fromName(name: String?): RosterVisibility = if (name == null) FULL else entries.find { it.name == name } ?: OWN_ONLY
    }
}

/**
 * SH-255: the employees read the whole roster directly ([RosterVisibility.FULL] and nobody hidden
 * in the roster for the others); otherwise their own shifts directly and the rest through the
 * server (the teamRoster function), which leaves out what they may not see.
 */
val WorkplaceSettings.teamSeesWholeRoster: Boolean
    get() = rosterVisibility == RosterVisibility.FULL && !rosterHasHiddenPeople

/**
 * SH-255: the shifts of one roster month [viewerId] (an employee) may see under [mode], as
 * functions/rosterPrivacy.js `visibleShifts` decides on the server: their own (planned, worked or
 * covered by them), the ones nobody is on, colleagues' where [mode] shows that day and nobody on
 * them is in [hidden], the ones offered to them by name, and the marketplace. The server also
 * leaves out the people hidden in the roster (the app does not know who they are: [hidden] is
 * empty there) and offers their marketplace shifts without a name.
 */
object RosterPrivacy {
    fun visibleTo(
        shifts: List<Shift>,
        viewerId: String,
        mode: RosterVisibility,
        hidden: Set<String> = emptySet()
    ): List<Shift> {
        val myDays = shifts.filter { it.userId == viewerId || it.actualUserId == viewerId }.mapTo(HashSet()) { it.date }
        fun visible(uid: String?, date: String): Boolean = uid == null || uid == viewerId ||
            (uid !in hidden && when (mode) {
                RosterVisibility.FULL -> true
                RosterVisibility.WORKING_WITH_ME -> date in myDays
                RosterVisibility.OWN_ONLY -> false
            })
        return shifts.filter { shift ->
            val own = shift.userId == viewerId || shift.actualUserId == viewerId || shift.unplanned?.userId == viewerId
            own || shift.swapRequest?.candidateUserId == viewerId ||
                listOf(shift.userId, shift.actualUserId, shift.unplanned?.userId).all { visible(it, shift.date) } ||
                shift.swapRequest?.status == SwapStatus.AVAILABLE
        }
    }
}
